import Echo from 'laravel-echo'
import io from 'socket.io-client'

class WebSocketService {
  constructor() {
    this.echo = null
    this.isConnected = false
    this.channels = new Map()
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectInterval = 5000
    this.store = null
  }

  // Initialize Echo connection
  init(store, token) {
    this.store = store
    
    try {
      window.io = io

      this.echo = new Echo({
        broadcaster: import.meta.env.VITE_BROADCAST_DRIVER || 'socket.io',
        host: import.meta.env.VITE_WEBSOCKET_URL || 'localhost:6001',
        auth: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        transports: ['websocket', 'polling'],
        upgrade: true,
        rememberUpgrade: true,
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        maxReconnectionAttempts: 5
      })

      this.setupConnectionEvents()
      console.log('WebSocket service initialized')
      
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error)
      this.handleConnectionError(error)
    }
  }

  // Setup connection event handlers
  setupConnectionEvents() {
    if (!this.echo || !this.echo.connector) return

    // Connection established
    this.echo.connector.socket.on('connect', () => {
      console.log('WebSocket connected')
      this.isConnected = true
      this.reconnectAttempts = 0
      
      if (this.store) {
        this.store.dispatch('session/setWebsocketConnected', true)
      }
    })

    // Connection lost
    this.echo.connector.socket.on('disconnect', () => {
      console.log('WebSocket disconnected')
      this.isConnected = false
      
      if (this.store) {
        this.store.dispatch('session/setWebsocketConnected', false)
      }
    })

    // Connection error
    this.echo.connector.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error)
      this.handleConnectionError(error)
    })

    // Reconnection attempts
    this.echo.connector.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`WebSocket reconnection attempt ${attemptNumber}`)
      this.reconnectAttempts = attemptNumber
    })

    this.echo.connector.socket.on('reconnect', () => {
      console.log('WebSocket reconnected')
      this.isConnected = true
      this.reconnectAttempts = 0
      
      if (this.store) {
        this.store.dispatch('session/setWebsocketConnected', true)
      }
    })

    this.echo.connector.socket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed')
      this.handleReconnectFailed()
    })
  }

  // Handle connection errors
  handleConnectionError(error) {
    console.error('WebSocket connection error:', error)
    this.isConnected = false
    
    if (this.store) {
      this.store.dispatch('session/setWebsocketConnected', false)
    }
  }

  // Handle reconnection failure
  handleReconnectFailed() {
    console.error('WebSocket failed to reconnect after maximum attempts')
    this.isConnected = false
    
    if (this.store) {
      this.store.dispatch('session/setWebsocketConnected', false)
    }
  }

  // Listen to student session events
  listenToStudentSession(pinCode, callbacks = {}) {
    if (!this.echo) {
      console.warn('WebSocket not initialized, cannot listen to student session')
      return null
    }

    const channelName = `quiz-session.${pinCode}`
    
    try {
      const channel = this.echo.channel(channelName)
      
      // Session started event
      if (callbacks.onSessionStarted) {
        channel.listen('SessionStarted', (data) => {
          console.log('Session started event received:', data)
          callbacks.onSessionStarted(data)
        })
      }

      // Session completed event  
      if (callbacks.onSessionCompleted) {
        channel.listen('SessionCompleted', (data) => {
          console.log('Session completed event received:', data)
          callbacks.onSessionCompleted(data)
        })
      }

      // Session status update
      if (callbacks.onSessionStatusUpdate) {
        channel.listen('SessionStatusUpdate', (data) => {
          console.log('Session status update received:', data)
          callbacks.onSessionStatusUpdate(data)
        })
      }

      this.channels.set(channelName, channel)
      console.log(`Listening to student session channel: ${channelName}`)
      
      return channel
    } catch (error) {
      console.error(`Failed to listen to student session ${pinCode}:`, error)
      return null
    }
  }

  // Listen to teacher session events
  listenToTeacherSession(pinCode, callbacks = {}) {
    if (!this.echo) {
      console.warn('WebSocket not initialized, cannot listen to teacher session')
      return null
    }

    const channelName = `quiz-session.${pinCode}.teacher`
    
    try {
      const channel = this.echo.private(channelName)
      
      // Student joined event
      if (callbacks.onStudentJoined) {
        channel.listen('StudentJoined', (data) => {
          console.log('Student joined event received:', data)
          callbacks.onStudentJoined(data)
        })
      }

      // Student left event
      if (callbacks.onStudentLeft) {
        channel.listen('StudentLeft', (data) => {
          console.log('Student left event received:', data)
          callbacks.onStudentLeft(data)
        })
      }

      // Student status update
      if (callbacks.onStudentStatusUpdate) {
        channel.listen('StudentStatusUpdate', (data) => {
          console.log('Student status update received:', data)
          callbacks.onStudentStatusUpdate(data)
        })
      }

      this.channels.set(channelName, channel)
      console.log(`Listening to teacher session channel: ${channelName}`)
      
      return channel
    } catch (error) {
      console.error(`Failed to listen to teacher session ${pinCode}:`, error)
      return null
    }
  }

  // Stop listening to a channel
  leaveChannel(channelName) {
    if (this.channels.has(channelName)) {
      const channel = this.channels.get(channelName)
      
      try {
        this.echo.leaveChannel(channelName)
        this.channels.delete(channelName)
        console.log(`Left channel: ${channelName}`)
      } catch (error) {
        console.error(`Failed to leave channel ${channelName}:`, error)
      }
    }
  }

  // Leave all channels
  leaveAllChannels() {
    for (const [channelName] of this.channels) {
      this.leaveChannel(channelName)
    }
  }

  // Disconnect WebSocket
  disconnect() {
    try {
      this.leaveAllChannels()
      
      if (this.echo && this.echo.disconnect) {
        this.echo.disconnect()
      }
      
      this.echo = null
      this.isConnected = false
      this.channels.clear()
      
      if (this.store) {
        this.store.dispatch('session/setWebsocketConnected', false)
      }
      
      console.log('WebSocket service disconnected')
    } catch (error) {
      console.error('Error disconnecting WebSocket:', error)
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      activeChannels: Array.from(this.channels.keys())
    }
  }

  // Force reconnection
  reconnect() {
    if (this.echo && this.echo.connector && this.echo.connector.socket) {
      try {
        this.echo.connector.socket.disconnect()
        this.echo.connector.socket.connect()
      } catch (error) {
        console.error('Failed to force reconnection:', error)
      }
    }
  }
}

// Create singleton instance
export const websocketService = new WebSocketService()
export default websocketService